import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
  Stethoscope, Loader2, AlertCircle, Info, CheckCircle2, XCircle, HelpCircle,
  CalendarClock, Layers, Copy, Check, ExternalLink,
  TrendingUp, TrendingDown, LineChart, Trash2, BarChart3,
} from 'lucide-react';
import { Card, CardTitle, CardDescription, Input, Button, Skeleton } from '@/components/ui';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useUserPrefs } from '@/lib/userPrefs';
import { useLocalStorageState } from '@/lib/useLocalStorageState';
import { getBlogTrend, recordBlogSnapshot, clearBlogTrend, type BlogSnapshot } from '@/lib/blogTrend';

type AnalyzeData = Awaited<ReturnType<typeof api.blogAnalysis.analyze>>;
type IndexState = 'pending' | 'indexed' | 'missing';

/**
 * Phase 61: 블로그 건강 진단 v2
 * 발행 통계 즉시 + 전체 글 색인 배치 로딩(스크롤) + 누락 정확 검출(logNo 매칭)
 */
export function BlogAnalysis() {
  const prefs = useUserPrefs();
  const [url, setUrl] = useLocalStorageState('blogAnalysis.url', prefs.defaultBlogUrl);
  const [data, setData] = useState<AnalyzeData | null>(null);
  // link → 색인 상태
  const [indexMap, setIndexMap] = useState<Record<string, { state: IndexState; position: number | null }>>({});
  const [indexProgress, setIndexProgress] = useState(0);
  const [indexBlocked, setIndexBlocked] = useState(false);
  const cancelRef = useRef(false);

  const analyze = useMutation({
    mutationFn: () => api.blogAnalysis.analyze(url.trim()),
    onSuccess: (d) => {
      setData(d);
      // 색인 상태 초기화 (전부 pending)
      const init: Record<string, { state: IndexState; position: number | null }> = {};
      d.posts.forEach((p) => (init[p.link] = { state: 'pending', position: null }));
      setIndexMap(init);
      setIndexProgress(0);
      setIndexBlocked(false);
      cancelRef.current = false;
      void runIndexChecks(d.posts);
    },
    onError: (e: Error) => alert(`분석 실패: ${e.message}`),
  });

  // 전체 글을 10개씩 배치로 색인 체크 (순차 — 타임아웃/부하 방지)
  const runIndexChecks = useCallback(async (posts: AnalyzeData['posts']) => {
    for (let i = 0; i < posts.length; i += 10) {
      if (cancelRef.current) return;
      const batch = posts.slice(i, i + 10);
      try {
        const res = await api.blogAnalysis.indexBatch(
          batch.map((p) => ({ title: p.title, link: p.link })),
          'blog',
        );
        if (res.blocked) {
          setIndexBlocked(true);
          return;
        }
        setIndexMap((prev) => {
          const next = { ...prev };
          for (const r of res.results) {
            next[r.link] = { state: r.indexed ? 'indexed' : 'missing', position: r.position };
          }
          return next;
        });
        setIndexProgress(Math.min(i + batch.length, posts.length));
      } catch {
        // 배치 실패 — 그 배치는 pending 유지, 다음 진행
      }
    }
  }, []);

  useEffect(() => () => { cancelRef.current = true; }, []);

  const canSubmit = !!url.trim() && !analyze.isPending;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-extrabold flex items-center gap-2">
          <Stethoscope className="h-7 w-7 text-gold" />
          <span className="text-gold-gradient">블로그 건강 진단</span>
          <span className="ml-2 rounded-full bg-gold/15 px-2 py-0.5 text-[11px] font-bold text-gold">NEW</span>
        </h1>
        <p className="mt-1 text-sm text-gray-400">
          발행 활동 + 주제 집중도 + <strong>전체 글의 검색 색인/누락</strong>을 정밀 진단합니다.
        </p>
      </header>

      <Card className="border-amber-400/30 bg-amber-400/5">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 shrink-0 text-amber-300 mt-0.5" />
          <p className="text-xs text-amber-100/80 leading-relaxed">
            RSS의 모든 글을 하나씩 네이버에서 검색해 <strong>그 글이 실제로 노출되는지(글 번호 정확 매칭)</strong>를 확인합니다.
            검색에 안 잡히면 <strong className="text-red-300">누락</strong>(저품질·스팸 판정·비공개·삭제 가능성). 글이 많으면 색인 검사가 순차로 진행돼요.
          </p>
        </div>
      </Card>

      <Card>
        <CardTitle>블로그 입력</CardTitle>
        <form
          className="mt-4 flex flex-col sm:flex-row gap-2"
          onSubmit={(e) => { e.preventDefault(); if (canSubmit) analyze.mutate(); }}
        >
          <Input
            className="flex-1"
            placeholder="예: blog.naver.com/myblog 또는 myblog"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            autoComplete="off"
          />
          <Button type="submit" variant="gold" disabled={!canSubmit}>
            {analyze.isPending ? <><Loader2 className="h-4 w-4 animate-spin" /> 분석 중…</> : <><Stethoscope className="h-4 w-4" /> 건강 진단</>}
          </Button>
        </form>
        {analyze.isError && (
          <p className="mt-3 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">
            <AlertCircle className="inline h-4 w-4 mr-1" />{(analyze.error as Error).message}
          </p>
        )}
      </Card>

      {analyze.isPending && (
        <Card><div className="grid grid-cols-2 md:grid-cols-4 gap-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}</div></Card>
      )}

      {data && (
        <Result
          data={data}
          indexMap={indexMap}
          indexProgress={indexProgress}
          indexBlocked={indexBlocked}
        />
      )}
    </div>
  );
}

function Result({
  data, indexMap, indexProgress, indexBlocked,
}: {
  data: AnalyzeData;
  indexMap: Record<string, { state: IndexState; position: number | null }>;
  indexProgress: number;
  indexBlocked: boolean;
}) {
  const { blog, publishing, topics, posts } = data;

  // 색인 집계
  const checked = posts.filter((p) => indexMap[p.link]?.state !== 'pending').length;
  const indexed = posts.filter((p) => indexMap[p.link]?.state === 'indexed').length;
  const missing = posts.filter((p) => indexMap[p.link]?.state === 'missing').length;
  const indexRate = checked > 0 ? Math.round((indexed / checked) * 100) : null;
  const done = indexProgress >= posts.length || indexBlocked;

  // 건강 점수 (발행 40 + 색인 40 + 주제 20)
  const pubScore = Math.min(40, Math.round((publishing.last30d / 12) * 40));
  const idxScore = indexRate != null && !indexBlocked ? Math.round((indexRate / 100) * 40) : 0;
  const topScore = Math.min(20, Math.round(((topics[0]?.pct ?? 0) / 60) * 20));
  const score = indexBlocked ? pubScore + topScore : pubScore + idxScore + topScore;
  const grade = score >= 80 ? 'A' : score >= 60 ? 'B' : score >= 40 ? 'C' : 'D';
  const gradeColor = grade === 'A' ? 'text-emerald-300' : grade === 'B' ? 'text-gold' : grade === 'C' ? 'text-amber-300' : 'text-red-300';

  return (
    <div className="space-y-4">
      {/* 건강 점수 */}
      <Card className="border-gold/40 bg-gradient-to-br from-gold/5 to-transparent">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <CardTitle>{blog.blogName ?? blog.userId} 건강 점수</CardTitle>
            <CardDescription className="mt-1">
              블로그 전체 {blog.postCount.toLocaleString()}개 중 최근 {posts.length}개 분석{' '}
              {done ? '· 색인 검사 완료' : `· 색인 검사 ${indexProgress}/${posts.length}…`}
            </CardDescription>
          </div>
          <div className="text-right">
            <div className={cn('text-5xl font-extrabold', gradeColor)}>{grade}</div>
            <div className="tabular text-sm text-gray-400">{score}/100</div>
          </div>
        </div>
      </Card>

      {/* 발행 활동 */}
      <Card>
        <CardTitle className="text-base flex items-center gap-2"><CalendarClock className="h-5 w-5 text-blue-300" /> 발행 활동</CardTitle>
        <CardDescription className="mt-1">최근 90일 내 글 {publishing.rssWindow}개 수집 기준 (블로그 전체 {blog.postCount.toLocaleString()}개)</CardDescription>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="최근 7일" value={`${publishing.last7d}회`} />
          <Stat label="최근 30일" value={`${publishing.last30d}회`} />
          <Stat label="최근 90일" value={`${publishing.last90d}회`} />
          <Stat label="평균 발행 간격" value={publishing.avgIntervalDays != null ? `${publishing.avgIntervalDays}일` : '-'} />
        </div>
        <div className="mt-2 text-xs text-gray-500">
          마지막 발행 {publishing.lastPublishedAgoDays != null ? `${publishing.lastPublishedAgoDays}일 전` : '-'}
          {publishing.oldestAgoDays != null && ` · RSS 최古 글 ${publishing.oldestAgoDays}일 전`}
        </div>
      </Card>

      {/* 포스팅 패턴 분석 (Phase 64) */}
      <PostingPattern
        posts={posts}
        userId={blog.userId}
        category={topics[0]?.category ?? ''}
      />

      {/* 색인율 */}
      <Card>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base">🔎 검색 색인율 {!done && <span className="text-xs text-gray-500">(검사 중…)</span>}</CardTitle>
          {indexRate != null && (
            <div className="text-sm">
              <strong className={cn(indexRate >= 80 ? 'text-emerald-300' : indexRate >= 50 ? 'text-amber-300' : 'text-red-300')}>{indexRate}%</strong>
              <span className="text-gray-500 ml-2">노출 {indexed} · <span className="text-red-300">누락 {missing}</span> / 검사 {checked}</span>
            </div>
          )}
        </div>
        {indexBlocked && (
          <p className="mt-2 text-xs text-amber-300">네이버 검색이 일시 제한돼 일부만 검사했습니다. 잠시 후 다시 시도하세요.</p>
        )}
        {/* 진행률 바 */}
        {!done && (
          <div className="mt-3 h-1.5 w-full rounded-full bg-gray-900 overflow-hidden">
            <div className="h-full bg-gold transition-[width] duration-300" style={{ width: `${(indexProgress / posts.length) * 100}%` }} />
          </div>
        )}

        {/* 전체 글 스크롤 리스트 */}
        <div className="mt-3 max-h-[28rem] overflow-y-auto rounded-lg border border-gray-800 divide-y divide-gray-900">
          {posts.map((p) => {
            const st = indexMap[p.link]?.state ?? 'pending';
            const pos = indexMap[p.link]?.position ?? null;
            return (
              <a
                key={p.link}
                href={p.link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5 px-3 py-2 hover:bg-gray-950/60"
              >
                {st === 'indexed' ? (
                  <CheckCircle2 className="h-4 w-4 text-emerald-300 shrink-0" />
                ) : st === 'missing' ? (
                  <XCircle className="h-4 w-4 text-red-300 shrink-0" />
                ) : (
                  <HelpCircle className="h-4 w-4 text-gray-600 shrink-0 animate-pulse" />
                )}
                <span className={cn('flex-1 truncate text-sm', st === 'missing' ? 'text-red-200' : 'text-gray-300')}>
                  {p.title}
                </span>
                {p.category && <span className="text-[10px] text-gray-600 shrink-0 hidden sm:inline">{p.category}</span>}
                <span className="text-[11px] text-gray-500 shrink-0 w-14 text-right">
                  {st === 'indexed' ? <span className="text-gold">{pos}위</span> : st === 'missing' ? <span className="text-red-300">누락</span> : '검사중'}
                </span>
              </a>
            );
          })}
        </div>
        <p className="mt-2 text-[11px] text-gray-600">
          누락 = 글 제목을 따옴표로 정확검색해도 그 글이 안 나옴 (색인 제외 상태). 클릭 시 원문 확인.
        </p>

        {missing >= 1 && done && (
          <IndexRequestHelper
            missingPosts={posts.filter((p) => indexMap[p.link]?.state === 'missing').map((p) => ({ title: p.title, link: p.link }))}
            missingCount={missing}
            checked={checked}
          />
        )}
      </Card>

      {/* 누락 추이 (진단할 때마다 자동 기록 — 로컬 저장) */}
      <IndexTrend
        userId={blog.userId}
        complete={indexProgress >= posts.length && !indexBlocked && checked > 0}
        snapshot={{
          total: blog.postCount,
          collected: posts.length,
          checked,
          indexed,
          missing,
          indexRate: indexRate ?? 0,
        }}
      />

      {/* 주제 분포 */}
      <Card>
        <CardTitle className="text-base flex items-center gap-2"><Layers className="h-5 w-5 text-purple-300" /> 주제 분포</CardTitle>
        <div className="mt-3 space-y-2">
          {topics.map((t) => (
            <div key={t.category} className="flex items-center gap-3">
              <span className="w-28 truncate text-sm text-gray-300 shrink-0">{t.category}</span>
              <div className="flex-1 h-5 rounded-md bg-gray-900 overflow-hidden">
                <div className="h-full bg-purple-500/60" style={{ width: `${Math.max(t.pct, 3)}%` }} />
              </div>
              <span className="tabular text-xs text-gray-400 w-16 text-right">{t.count}개 {t.pct}%</span>
            </div>
          ))}
        </div>
      </Card>

      <p className="text-xs text-gray-600 text-center">
        <Link to="/blog-coach" className="text-gold hover:underline">블로그 코치로 키워드 추천받기 →</Link>
      </p>
    </div>
  );
}

// ─── 누락 추이 그래프 (Phase 63) ────────────────────────────
// 진단할 때마다 결과 스냅샷을 localStorage 에 자동 누적 → 누락이 줄어드는지 추적.
// 같은 날 재진단은 그 날 값으로 덮어씀. 서버 저장 없음.

function IndexTrend({
  userId,
  complete,
  snapshot,
}: {
  userId: string;
  complete: boolean;
  snapshot: { total: number; collected: number; checked: number; indexed: number; missing: number; indexRate: number };
}) {
  const [trend, setTrend] = useState<BlogSnapshot[]>(() => getBlogTrend(userId));

  // 진단이 완료되면(차단 아님 + 검사>0) 오늘 스냅샷 기록
  useEffect(() => {
    if (!complete || snapshot.checked <= 0) return;
    setTrend(recordBlogSnapshot(userId, snapshot));
    // 의존성은 원시값만 — 객체 재생성으로 인한 중복 기록 방지
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complete, userId, snapshot.checked, snapshot.indexed, snapshot.missing]);

  // 블로그가 바뀌면 그 블로그의 추이로 교체
  useEffect(() => {
    setTrend(getBlogTrend(userId));
  }, [userId]);

  if (trend.length === 0) return null;

  const first = trend[0]!;
  const latest = trend[trend.length - 1]!;
  const missingDelta = latest.missing - first.missing; // 음수 = 개선
  const rateDelta = latest.indexRate - first.indexRate; // 양수 = 개선
  const single = trend.length < 2;
  const improving = rateDelta > 0 || missingDelta < 0;

  const BAR_H = 120;
  const view = trend.slice(-24); // 최근 24회만 표시

  function reset() {
    if (!confirm('이 블로그의 누락 추이 기록을 모두 지울까요? (브라우저에서만 삭제)')) return;
    clearBlogTrend(userId);
    setTrend([]);
  }

  return (
    <Card>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <CardTitle className="text-base flex items-center gap-2">
          <LineChart className="h-5 w-5 text-emerald-300" /> 누락 추이
          <span className="text-[11px] font-normal text-gray-500">진단 {trend.length}회 기록</span>
        </CardTitle>
        <button
          type="button"
          onClick={reset}
          className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] text-gray-500 hover:text-red-300 hover:bg-red-500/10"
        >
          <Trash2 className="h-3 w-3" /> 기록 지우기
        </button>
      </div>

      {single ? (
        <p className="mt-2 text-xs text-gray-400">
          첫 진단을 기록했습니다. <strong className="text-gray-200">다음에 다시 진단하면</strong> 누락이 줄었는지 그래프로 비교됩니다.
          제목 개선·색인요청 후 며칠 뒤 재진단해 보세요.
        </p>
      ) : (
        <div
          className={cn(
            'mt-2 inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-semibold',
            improving ? 'bg-emerald-400/15 text-emerald-200' : missingDelta > 0 ? 'bg-red-500/15 text-red-200' : 'bg-gray-800 text-gray-300',
          )}
        >
          {improving ? <TrendingDown className="h-3.5 w-3.5" /> : missingDelta > 0 ? <TrendingUp className="h-3.5 w-3.5" /> : null}
          {missingDelta === 0
            ? '첫 진단 대비 누락 수 변화 없음'
            : `첫 진단 대비 누락 ${missingDelta > 0 ? '+' : ''}${missingDelta}개`}
          <span className="font-normal text-gray-400">· 색인율 {first.indexRate}% → {latest.indexRate}%</span>
        </div>
      )}

      {/* 막대: 각 진단을 자기 검사수 기준 100% 로 정규화 → 색인(초록)/누락(빨강) 비율 */}
      <div className="mt-3 flex items-end gap-1.5 overflow-x-auto pb-1">
        {view.map((s) => {
          const missPct = s.checked > 0 ? (s.missing / s.checked) * 100 : 0;
          const md = s.date.slice(5).replace('-', '/');
          return (
            <div key={s.date} className="flex flex-col items-center gap-1 shrink-0 w-9" title={`${s.date} · 색인 ${s.indexed} / 누락 ${s.missing} / 검사 ${s.checked}`}>
              <div className="tabular text-[9px] font-bold text-red-300">{s.missing}</div>
              <div className="flex w-5 flex-col overflow-hidden rounded-sm bg-gray-900" style={{ height: BAR_H }}>
                <div className="w-full bg-red-500/70" style={{ height: `${missPct}%` }} />
                <div className="w-full bg-emerald-500/55" style={{ height: `${100 - missPct}%` }} />
              </div>
              <div className="tabular text-[9px] text-gray-500">{md}</div>
            </div>
          );
        })}
      </div>
      <div className="mt-1.5 flex items-center gap-3 text-[10px] text-gray-500">
        <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-red-500/70" /> 누락</span>
        <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-sm bg-emerald-500/55" /> 색인</span>
        <span>· 막대 위 숫자 = 그 날 누락 글 수 · 막대 높이는 검사 대비 비율</span>
      </div>
    </Card>
  );
}

// ─── 색인 요청 도우미 (Phase 62) ────────────────────────────

function IndexRequestHelper({
  missingPosts,
  missingCount,
  checked,
}: {
  missingPosts: Array<{ title: string; link: string }>;
  missingCount: number;
  checked: number;
}) {
  const [copied, setCopied] = useState(false);
  const [page, setPage] = useState(0);
  const PER = 50; // 서치어드바이저 일일 수집요청 한도 고려
  const missingLinks = missingPosts.map((p) => p.link);
  const totalPages = Math.ceil(missingLinks.length / PER);
  const slice = missingLinks.slice(page * PER, page * PER + PER);
  const ratio = checked > 0 ? Math.round((missingCount / checked) * 100) : 0;

  async function copy() {
    const text = slice.join('\n');
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); } catch { /* */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  }

  return (
    <div className="mt-3 rounded-lg border border-amber-400/30 bg-amber-400/5 p-3.5">
      <div className="text-sm font-bold text-amber-200">
        🛠️ 색인 요청 도우미 — 누락 {missingCount}개
      </div>

      {ratio >= 50 && (
        <p className="mt-1.5 text-xs text-red-200/90">
          ⚠️ 누락률 {ratio}% 로 매우 높습니다. 단순 미수집보다 <strong>저품질·유사문서 판정</strong> 가능성이 큽니다.
          색인요청과 함께 <strong>제목·본문 품질 개선</strong>을 병행해야 재누락을 막습니다.
        </p>
      )}

      {/* 1단계: URL 일괄 복사 */}
      <div className="mt-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <span className="text-xs text-amber-100/80">
            ① 누락 글 URL 복사 {totalPages > 1 && `(${page + 1}/${totalPages} 묶음 · 50개씩)`}
          </span>
          <button
            type="button"
            onClick={copy}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-semibold',
              copied ? 'border-emerald-400/60 bg-emerald-400/15 text-emerald-200' : 'border-gold/40 bg-gold/10 text-gold hover:bg-gold/20',
            )}
          >
            {copied ? <><Check className="h-3 w-3" /> 복사됨!</> : <><Copy className="h-3 w-3" /> URL {slice.length}개 복사</>}
          </button>
        </div>
        {totalPages > 1 && (
          <div className="mt-1.5 flex gap-1 flex-wrap">
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setPage(i)}
                className={cn(
                  'rounded px-2 py-0.5 text-[11px]',
                  page === i ? 'bg-gold/20 text-gold font-bold' : 'bg-gray-800 text-gray-400',
                )}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* 2단계: 서치어드바이저 */}
      <div className="mt-3">
        <span className="text-xs text-amber-100/80">② 네이버 서치어드바이저에서 수집 요청 (붙여넣기)</span>
        <a
          href="https://searchadvisor.naver.com/console/board/request-crawl"
          target="_blank"
          rel="noopener noreferrer"
          className="mt-1.5 inline-flex items-center gap-1.5 rounded-md border border-gold/40 bg-gold/10 px-3 py-1.5 text-xs font-semibold text-gold hover:bg-gold/20"
        >
          서치어드바이저 수집요청 열기 <ExternalLink className="h-3 w-3" />
        </a>
        <p className="mt-1 text-[11px] text-amber-100/60">
          로그인 → 내 블로그 사이트 선택 → "요청 → 웹페이지 수집"에 복사한 URL 붙여넣기. 일일 한도가 있어 50개씩 나눴습니다.
        </p>
      </div>

      {/* 품질 개선 */}
      <div className="mt-3 border-t border-amber-400/20 pt-2.5">
        <span className="text-xs text-amber-100/80">③ 재누락 방지 — 품질 개선 (병행 필수)</span>
        <ul className="mt-1 space-y-0.5 text-[11px] text-amber-100/70 list-disc list-inside">
          <li>정형화된 제목 패턴 탈피 ("OO, OO의 N가지" 반복 금지 — 유사문서 판정 위험)</li>
          <li>본문 1500자+ · 직접 작성한 경험/사진 · 외부 복붙 제거</li>
          <li>하루 1~2개씩 수정 재발행 (대량 일괄 수정은 어뷰징 의심)</li>
          <li>발행 직후 글은 색인 전일 수 있음 — 1주일 뒤 재확인</li>
        </ul>
      </div>

      {/* ④ AI 제목 개선 */}
      <Retitler missingPosts={missingPosts} />
    </div>
  );
}

// ─── AI 제목 개선 (Phase 62) ────────────────────────────────

function Retitler({ missingPosts }: { missingPosts: Array<{ title: string; link: string }> }) {
  const prefs = useUserPrefs();
  const [results, setResults] = useState<Array<{ original: string; suggestions: string[]; link?: string }>>([]);
  const [batch, setBatch] = useState(0);
  const BATCH = 10;
  const totalBatches = Math.ceil(missingPosts.length / BATCH);

  const m = useMutation({
    mutationFn: () => {
      const slice = missingPosts.slice(batch * BATCH, batch * BATCH + BATCH);
      return api.blogAnalysis.retitle(slice.map((p) => p.title), prefs.geminiApiKey);
    },
    onSuccess: (d) => {
      // 원본 → link 매핑
      const linkByTitle = new Map(missingPosts.map((p) => [p.title, p.link]));
      setResults(d.results.map((r) => ({ ...r, link: linkByTitle.get(r.original) })));
    },
    onError: (e: Error) => alert(`제목 개선 실패: ${e.message}`),
  });

  return (
    <div className="mt-3 border-t border-amber-400/20 pt-2.5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <span className="text-xs text-amber-100/80">
          ④ ✏️ AI 제목 개선 — 양산형 제목을 자연스럽게 다시 짓기
          {totalBatches > 1 && ` (${batch + 1}/${totalBatches} 묶음 · 10개씩)`}
        </span>
        <div className="flex items-center gap-1.5">
          {totalBatches > 1 && (
            <select
              value={batch}
              onChange={(e) => setBatch(Number(e.target.value))}
              className="h-7 rounded border border-gray-700 bg-gray-950 px-1.5 text-[11px] text-gray-300"
            >
              {Array.from({ length: totalBatches }).map((_, i) => (
                <option key={i} value={i}>{i + 1}번 묶음</option>
              ))}
            </select>
          )}
          <button
            type="button"
            onClick={() => {
              if (!prefs.geminiApiKey) { alert('설정에서 Gemini API 키를 먼저 저장하세요.'); return; }
              m.mutate();
            }}
            disabled={m.isPending}
            className="inline-flex items-center gap-1.5 rounded-md border border-purple-400/50 bg-purple-400/10 px-2.5 py-1 text-xs font-semibold text-purple-200 hover:bg-purple-400/20 disabled:opacity-50"
          >
            {m.isPending ? <><Loader2 className="h-3 w-3 animate-spin" /> 생성 중…</> : 'AI로 제목 다시 짓기'}
          </button>
        </div>
      </div>
      {!prefs.geminiApiKey && (
        <p className="mt-1 text-[11px] text-amber-100/50">
          ※ 본인 Gemini 키 필요 — <Link to="/settings" className="text-gold hover:underline">설정에서 저장</Link>
        </p>
      )}

      {results.length > 0 && (
        <div className="mt-2.5 space-y-2.5">
          {results.map((r, i) => (
            <div key={i} className="rounded-lg border border-gray-800 bg-gray-950/60 p-2.5">
              <div className="text-[11px] text-gray-500 line-through truncate">{r.original}</div>
              <div className="mt-1.5 space-y-1">
                {r.suggestions.map((s, si) => (
                  <SuggestionRow key={si} text={s} />
                ))}
              </div>
              {r.link && (
                <a href={r.link} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex items-center gap-0.5 text-[10px] text-gold hover:underline">
                  원문 열어서 제목 수정 <ExternalLink className="h-2.5 w-2.5" />
                </a>
              )}
            </div>
          ))}
          <p className="text-[10px] text-amber-100/50">
            마음에 드는 제목을 복사 → 네이버 블로그 글 편집에서 제목 교체 후 저장하면 재색인을 유도합니다.
          </p>
        </div>
      )}
    </div>
  );
}

function SuggestionRow({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try { await navigator.clipboard.writeText(text); } catch { /* */ }
    setCopied(true); setTimeout(() => setCopied(false), 1400);
  }
  return (
    <div className="flex items-center gap-2">
      <span className="flex-1 text-sm text-white">{text}</span>
      <button
        type="button"
        onClick={copy}
        className={cn('inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] shrink-0',
          copied ? 'bg-emerald-400/15 text-emerald-200' : 'bg-gray-800 text-gray-300 hover:bg-gray-700')}
      >
        {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
      </button>
    </div>
  );
}

// ─── 포스팅 패턴 분석 (Phase 64) ────────────────────────────
// 최근 글 샘플링 → 제목 길이, 본문 길이, 이미지 수를 상위권 평균과 비교.

type PostStatsData = Awaited<ReturnType<typeof api.blogAnalysis.postStats>>;

function PostingPattern({
  posts,
  userId,
  category,
}: {
  posts: Array<{ title: string; link: string; category: string | null; publishedAt: number }>;
  userId: string;
  category: string;
}) {
  const [stats, setStats] = useState<PostStatsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const triggered = useRef(false);

  useEffect(() => {
    if (posts.length === 0 || triggered.current) return;
    triggered.current = true;

    const logNos = posts
      .slice(0, 10)
      .map((p) => p.link.match(/\/(\d+)$/)?.[1])
      .filter((n): n is string => !!n);
    if (logNos.length === 0) return;

    setLoading(true);
    api.blogAnalysis
      .postStats(userId, logNos, category)
      .then(setStats)
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [posts, userId, category]);

  // 제목 길이: 프론트에서 직접 계산 (API 불필요)
  const sampleTitles = posts.slice(0, 10);
  const avgTitleLength =
    sampleTitles.length > 0
      ? Math.round(sampleTitles.reduce((s, p) => s + p.title.length, 0) / sampleTitles.length)
      : 0;

  if (loading) {
    return (
      <Card>
        <CardTitle className="text-base flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-sky-300" /> 포스팅 패턴 분석
        </CardTitle>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-32 w-full" />)}
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardTitle className="text-base flex items-center gap-2">
          <BarChart3 className="h-5 w-5 text-sky-300" /> 포스팅 패턴 분석
        </CardTitle>
        <p className="mt-2 text-sm text-red-300">분석 실패: {error}</p>
      </Card>
    );
  }

  if (!stats || stats.valid === 0) return null;

  const { averages, benchmarks } = stats;
  const metrics: PatternMetricProps[] = [
    {
      label: '제목 길이',
      unit: '자',
      myValue: avgTitleLength,
      benchmark: benchmarks.titleLength,
      desc: avgTitleLength > benchmarks.titleLength
        ? `상위권 평균 대비 ${avgTitleLength - benchmarks.titleLength}자 깁니다`
        : avgTitleLength < benchmarks.titleLength
          ? `상위권 평균 대비 ${benchmarks.titleLength - avgTitleLength}자 짧습니다`
          : '상위권 평균과 같습니다',
    },
    {
      label: '본문 길이',
      unit: '자',
      myValue: averages.bodyLength,
      benchmark: benchmarks.bodyLength,
      desc: averages.bodyLength > benchmarks.bodyLength
        ? `상위권 평균 대비 ${(averages.bodyLength - benchmarks.bodyLength).toLocaleString()}자 깁니다`
        : averages.bodyLength < benchmarks.bodyLength
          ? `상위권 평균 대비 ${(benchmarks.bodyLength - averages.bodyLength).toLocaleString()}자 짧습니다`
          : '상위권 평균과 같습니다',
    },
    {
      label: '이미지 수',
      unit: '장',
      myValue: Math.round(averages.imageCount),
      benchmark: benchmarks.imageCount,
      desc: averages.imageCount > benchmarks.imageCount
        ? `상위권 평균 대비 ${Math.round(averages.imageCount - benchmarks.imageCount)}장 많습니다`
        : averages.imageCount < benchmarks.imageCount
          ? `상위권 평균 대비 ${Math.round(benchmarks.imageCount - averages.imageCount)}장 적습니다`
          : '상위권 평균과 같습니다',
    },
  ];

  return (
    <Card>
      <CardTitle className="text-base flex items-center gap-2">
        <BarChart3 className="h-5 w-5 text-sky-300" /> 포스팅 패턴 분석
      </CardTitle>
      <CardDescription className="mt-1">
        최근 {stats.sampled}개 글을 {stats.category} 카테고리 상위 노출 블로그와 비교
      </CardDescription>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-6">
        {metrics.map((m) => (
          <PatternMetric key={m.label} {...m} />
        ))}
      </div>
    </Card>
  );
}

interface PatternMetricProps {
  label: string;
  unit: string;
  myValue: number;
  benchmark: number;
  desc: string;
}

function PatternMetric({ label, unit, myValue, benchmark, desc }: PatternMetricProps) {
  const ratio = benchmark > 0 ? myValue / benchmark : 0;
  const rating = ratio >= 1.15 ? '평균 이상' : ratio >= 0.85 ? '평균' : '평균 이하';
  const ratingColor =
    ratio >= 1.15 ? 'text-emerald-300' : ratio >= 0.85 ? 'text-amber-300' : 'text-red-300';
  const barColor =
    ratio >= 1.15 ? 'bg-emerald-500/70' : ratio >= 0.85 ? 'bg-amber-500/70' : 'bg-red-500/70';

  const maxVal = Math.max(myValue, benchmark, 1);
  const benchPct = (benchmark / maxVal) * 100;
  const myPct = (myValue / maxVal) * 100;

  return (
    <div className="rounded-xl border border-gray-800 bg-gray-950/50 p-4">
      <div className="text-center">
        <div className="text-sm font-bold text-white">{label}</div>
        <div className={cn('text-xs font-semibold mt-0.5', ratingColor)}>{rating}</div>
      </div>
      <p className="mt-1 text-center text-[10px] text-gray-500 leading-snug">{desc}</p>
      <div className="mt-3 space-y-2.5">
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] text-gray-500">상위권 평균</span>
            <span className="tabular text-xs text-gray-400">
              {benchmark.toLocaleString()}{unit}
            </span>
          </div>
          <div className="h-5 w-full rounded bg-gray-900 overflow-hidden">
            <div
              className="h-full rounded bg-gray-600 transition-[width] duration-500"
              style={{ width: `${Math.max(benchPct, 4)}%` }}
            />
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[10px] text-gray-500">나의 평균</span>
            <span className={cn('tabular text-xs font-bold', ratingColor)}>
              {myValue.toLocaleString()}{unit}
            </span>
          </div>
          <div className="h-5 w-full rounded bg-gray-900 overflow-hidden">
            <div
              className={cn('h-full rounded transition-[width] duration-500', barColor)}
              style={{ width: `${Math.max(myPct, 4)}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, tone = 'gray' }: { label: string; value: string; tone?: 'gray' | 'red' }) {
  return (
    <div className="rounded-lg border border-gray-800 bg-gray-950 px-3 py-2.5">
      <div className="text-[10px] uppercase tracking-widest text-gray-500">{label}</div>
      <div className={cn('tabular text-lg font-bold mt-0.5', tone === 'red' ? 'text-red-300' : 'text-white')}>{value}</div>
    </div>
  );
}
